import clsx from "clsx";
import { sum } from "lodash";
import { UIEventHandler, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from 'mobx-react';
import { Column } from '@bst/react-data-grid';
import { DataGrid, renderRow, useLoadingRows } from "@bst/react-ui";

import { UserDTO } from "../../../apiclients/bstinsights";
import ProjectHeader from "../../ProjectHeader/Views/ProjectHeader";
import ToastNotification from "../../ToastNotification/ToastNotification";
import { ColumnFilterMenu } from "./ColumnFilterMenu";
import { UserManagementFooter } from "./UserManagementFooter";
import { UserManagementHeader } from "./UserManagementHeader";
import { InvitationModal } from "./InvitationModal";
import { EmptyGridMessage } from './EmptyGridMessage';
import { AddUserForm } from "./AddUserForm/AddUserForm";
import { useColumns } from "../Hooks/useColumns";
import { useUserManagement } from '../Hooks/useUserManagement';
import { useInvitationQueue } from '../Hooks/useInvitationQueue';
import { ROW_HEIGHT, COLUMN_WIDTH } from '../Hooks/utils/constants';
import { useStores } from '../../../stores/useStores';

const columnsTotalWidth = (columns: Column<UserDTO, unknown>[]) => sum(columns.map((column) => column.width || column.minWidth || COLUMN_WIDTH))

export const UserManagement = observer(() => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null)
  const horizontalScrollRef = useRef<HTMLDivElement>(null)
  const [toastVisible, setToastVisible] = useState(false);
  const [toastContent, setToastContent] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const { userManagementStore } = useStores();

  useEffect(() => {
    return () => {
      // Clear filters when UserManagement modal is closed
      userManagementStore.resetAll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    data,
    isFetching,
    isRefetching,
    loadNextPage,
    handleInviteUser,
    handleRowsChange
  } = useUserManagement();

  const {
    sort,
    gridRef,
    columns,
    selected,
    comboboxItems,
    showColumnMenu,
    anchorPosition,
    selectedCount,
    invitationStrategy,
    getRowClass,
    onColumnsReorder,
    setShowColumnMenu,
    handleColumnResizeEnd,
    handleColumnVisibility,
    handleClearSelectedRows,
  } = useColumns(data);

  const calculatedColumns = useMemo(() => {
    const columnsWidth = columnsTotalWidth(columns)
    const hasSpace = window.innerWidth - columnsWidth > 50;
    if (hasSpace) {
      return [
        ...columns,
        {
          key: "EMPTY_CELL",
          name: "",
          width: "auto"
        }
      ]
    }
    return columns
  }, [columns])

  const horizontalScrollWidth = useMemo(() => columnsTotalWidth(columns) + 50, [columns])

  const { enqueueInvitedHandles: enqueueInvitedUsers, showSuccess } =
    useInvitationQueue();

  // Producer: Add email to queue
  const handleInviteUsersProducer = async () => {
    const invitedUserIds: number[] | undefined = await handleInviteUser(
      selected,
      invitationStrategy,
      handleClearSelectedRows
    );
    enqueueInvitedUsers(invitedUserIds ?? []);
  };

  // istanbul ignore next
  const isAtBottom = (
    { currentTarget }: React.UIEvent<HTMLDivElement>,
    handicap: number = 0
  ) => currentTarget.scrollTop + handicap >= currentTarget.scrollHeight - currentTarget.clientHeight;

  // To track horizontal scroll
  let leftScrollPosition = 0;

  // istanbul ignore next
  const handleGridScroll = async (event: React.UIEvent<HTMLDivElement>) => {
    const { currentTarget } = event;
    // If it's horizontal scroll
    if (currentTarget.scrollLeft !== leftScrollPosition) {
      // Track further and scroll Horizontal scrollbar
      leftScrollPosition = currentTarget.scrollLeft;
      horizontalScrollRef?.current?.scrollTo({ left: currentTarget.scrollLeft })
    } else {
      containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" })
    }
    if (isAtBottom(event, ROW_HEIGHT)) {
      await loadNextPage()
    }
  };

  const rows = useLoadingRows({
    rows: data,
    loadingRowsCount: 10,
    isLoading: isFetching && !isRefetching
  }) as UserDTO[]

  const handleHorizontalScroll: UIEventHandler<HTMLDivElement> = (event) => {
    if (gridRef.current) {
      gridRef.current.element?.scrollTo({ left: event.currentTarget.scrollLeft })
    }
  }

  return (
    <>
      <div ref={containerRef} className="user-management">
        {showSuccess && (
          <div className="alert alert--success position-relative">
            <i className="icon icon-check"></i>
            <span>{t("userManagementPage.operation_completed_message")}</span>
          </div>
        )}
        <ColumnFilterMenu
          show={showColumnMenu}
          setShow={setShowColumnMenu}
          columns={comboboxItems}
          menuPosition={anchorPosition}
          handleColumnVisibility={handleColumnVisibility}
        />

        <UserManagementHeader />

        <div className="user-management-body">
          <ProjectHeader
            className="user-management-subheader"
            title={t("userManagementPage.allUsers_title")}
            hasAddButton={true}
            hasTitle={true}
            id="titlePage"
            addButtonHandler={() => setShowAddUserModal(true)}
          />
          <DataGrid
            ref={gridRef}
            rows={userManagementStore.hideAllRows() ? [] : rows}
            className={clsx(
              "user-management-grid",
              selectedCount && "user-management-grid--footer-active"
            )}
            rowClass={getRowClass}
            data-testid="user-management-grid"
            columns={calculatedColumns}
            sortColumns={sort ? [sort] : []}
            onScroll={handleGridScroll}
            rowHeight={ROW_HEIGHT}
            rowKeyGetter={(row) => row.userId as string}
            onRowsChange={handleRowsChange}
            onColumnsReorder={onColumnsReorder}
            renderers={{
              renderRow,
              noRowsFallback: <EmptyGridMessage />
            }}
            onColumnResizeEnd={handleColumnResizeEnd}
            defaultColumnOptions={{
              resizable: true,
              sortable: true,
              width: COLUMN_WIDTH,
            }}
          />
          {selectedCount > 0 && (
            <UserManagementFooter
              selectedCount={selectedCount}
              handleInviteUsers={handleInviteUsersProducer}
              setShowInvitationModal={setShowInvitationModal}
            />
          )}
          <div ref={horizontalScrollRef} className="user-management-grid-horizontal-scroll" onScroll={handleHorizontalScroll}>
            <div style={{ width: horizontalScrollWidth }} />
          </div>
        </div>
      </div>
      <AddUserForm
        showAddUserModal={showAddUserModal}
        setToastVisible={setToastVisible}
        setToastContent={setToastContent}
        setShowAddUserModal={setShowAddUserModal}
      />

      <InvitationModal
        show={showInvitationModal}
        setShowInvitationModal={setShowInvitationModal}
      />

      <ToastNotification
        visibility={toastVisible}
        content={toastContent}
        timeOut={3000}
        position={{ X: "Right", Y: "Bottom" }}
      />
    </>
  );
});

export default UserManagement;
